import {
  MusicNote as MusicIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Chip,
  Container,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetchArtists, ArtistSummary } from "../utils/useDb";

export default function ArtistsPage() {
  const [artists, setArtists] = useState<ArtistSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      try {
        const list = await apiFetchArtists();
        setArtists(list);
      } catch (e) {
        console.error("Failed to load artists:", e);
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const filteredArtists = useMemo(() => {
    if (!searchTerm.trim()) return artists;
    const query = searchTerm.trim().toLowerCase();
    return artists.filter((a) => a.name.toLowerCase().includes(query));
  }, [artists, searchTerm]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Box textAlign="center" mb={5}>
            <Typography
              variant="h3"
              component="h1"
              fontWeight="bold"
              color="black"
            >
              Browse{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg, #1976d2, #7c3aed)",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Artists
              </Box>
            </Typography>
            <Typography variant="h6" color="textSecondary" mt={1}>
              Find chords and tabs by your favorite musicians and bands
            </Typography>
          </Box>
        </motion.div>

        {/* Search Bar */}
        <Card variant="outlined" sx={{ mb: 4, p: 2, borderRadius: 2 }}>
          <Box position="relative" display="flex" alignItems="center">
            <SearchIcon
              sx={{
                position: "absolute",
                left: 12,
                color: "text.disabled",
                pointerEvents: "none",
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search artists..."
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              InputProps={{ sx: { pl: 4.5 } }}
            />
          </Box>
        </Card>

        {/* Results Header */}
        <Box mb={2} px={0.5}>
          <Typography variant="h6" fontWeight="bold">
            All Artists ({filteredArtists.length})
          </Typography>
        </Box>

        {/* Artists Grid */}
        {isLoading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: "grey.300",
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          height: 18,
                          bgcolor: "grey.300",
                          borderRadius: 1,
                          mb: 1,
                          width: "70%",
                        }}
                      />
                      <Box
                        sx={{
                          height: 14,
                          bgcolor: "grey.200",
                          borderRadius: 1,
                          width: "40%",
                        }}
                      />
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : filteredArtists.length > 0 ? (
          <Grid
            container
            spacing={2}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {filteredArtists.map((artist, idx) => {
              const initials = (artist.name || "A")
                .split(" ")
                .map((w) => w[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={artist.name}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(idx * 0.02, 0.4) }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2.5,
                        background: theme.palette.background.paper,
                        ":hover": {
                          boxShadow: "0 6px 20px rgba(37, 99, 235, 0.12)",
                          borderColor: "primary.main",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <CardActionArea
                        component={Link}
                        to={`/Artist?name=${encodeURIComponent(artist.name)}`}
                        sx={{ p: 2 }}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              fontWeight: "bold",
                              fontSize: "1.1rem",
                              background:
                                "linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)",
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Box sx={{ overflow: "hidden" }}>
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              noWrap
                              title={artist.name}
                            >
                              {artist.name}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                              <Chip
                                icon={<MusicIcon sx={{ fontSize: 13 }} />}
                                label={`${artist.song_count} ${
                                  artist.song_count === 1 ? "song" : "songs"
                                }`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "0.75rem" }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Card
            variant="outlined"
            sx={{ textAlign: "center", p: 6, borderRadius: 2 }}
          >
            <PersonIcon color="disabled" sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="h6" fontWeight="medium">
              No artists found
            </Typography>
            <Typography color="text.secondary">
              Try adjusting your search query
            </Typography>
          </Card>
        )}
      </Container>
    </Box>
  );
}
